export const OFF_MARKET_SAVED_SEARCH_MODEL = 'Connect::OffMarketSavedSearch' as const;
export const EXCLUSIVE_LISTING_SAVED_SEARCH_MODEL = 'Connect::BuildoutSavedSearch' as const;
export const CONNECT_PERSON_TYPE = 'Connect::Person' as const;
export const CONNECT_COMPANY_TYPE = 'Connect::Company' as const;

export type Note = {
  id: number;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export type ExportCreditCounts = {
  callListSize: number;
  availableCredits: number;
  creditsForExport: number;
}

export type DataExportTemplate = {
  id: number;
  name: string;
  dataExportType: string;
  createdAt: string;
  columns: string[];
}

export type DataExport = {
  createdAt: string;
  id: number;
  lastDownloaded?: string;
  name: string;
}

export type DataExportTemplateForm = {
  name: string;
  dataExportType?: string;
}

export type DataExportTemplateColumnOptions = {
  property: { value: string; label: string; }[];
  activity: { value: string; label: string; }[];
}

export type CallListItem = {
  id: number;
  callList: CallList;
  callListableType: string;
  callListableId: number;
  callListable: CallListable;
  displayName: string;
  restricted: boolean;
  insightsPropertyId?: string;
  viewedAt: string | null;
  createdAt: string;
  numContacts: number;
}

export type CallListable = {
  id: number;
  lastUpdated?: string;
  name: string;
  streetviewUrl: string | null;
  propertyId: string;
  propertyTypeId: number;
  scores: InsightsPropertyScores;
}

export type CallProgress = {
  contacted: number;
}

export type ExclusiveListing = {
  addressLineOne: string;
  addressLineTwo: string;
  attributesTable: string[];
  interested: boolean;
  location: { lat: number; lng: number; };
  priceSummary: { difference?: string; value: string; };
  recentlyUpdatedLabel: string | null;
  saleListingId: number;
  saved: boolean;
  thumbnail: string;
  title: string;
  underContract: boolean;
  usingPlaceholderImage: boolean;
}

export type OwnedProperty = InsightsProperty & {
  ownershipId: number;
}

export type ConnectProperty = InsightsProperty & {
  id: number;
  insightsPropertyId: string;
  address: string;
  bookmarked: boolean;
  city: string;
  state: string;
  tags: string[];
  zip: string;
}

type InsightsBrokerInfo = {
  brokerage: string | null;
  email: string | null;
  name: string | null;
  phone: string | null;
}

export type InsightsListing = {
  brokerInfo: InsightsBrokerInfo[];
  forLease: boolean;
  forSale: boolean;
  originalListingDate: string | null;
  salePrice: number;
  soldDate: string | null;
  soldPrice: number;
}

export type InsightsProperty = {
  address: string;
  bookmarked?: boolean;
  buildingSizeSf?: number;
  city: string;
  commercialUnits?: number;
  highlights: { label: string; value: string }[];
  lastSaleDate?: string;
  lastSalePrice?: number;
  lastUpdated: string;
  latitude: number;
  listings: InsightsListing;
  longitude: number;
  lotSizeSf?: number;
  propertyId: string;
  propertyType: string;
  propertyTypeId: number;
  residentialUnits?: number;
  scores: InsightsPropertyScores;
  state: string;
  streetviewUrl: string;
  valuation? : { estimate?: number };
  zip: string;
};

export type AllOffMarketResults = {
  propertyId: string;
  latitude: number;
  longitude: number;
  propertyTypeId: number;
  parcelBoundary: any;
  valuationEstimate: any;
  listings: any;
  buildingClass: string;
  restricted: boolean;
};

export type AllExclusiveListingResults = {
  location: { lat: number, lon: number; };
  propertyTypeId: number;
  saleListingId: number;
}


export type InsightsPropertyScores = {
  likelyToSell: LikelyToSellRefi | null;
  likelyToRefi: LikelyToSellRefi | null;
}

export type LikelyToSellRefi = {
  restricted?: boolean;
  rank: number;
}

export type CallList = {
  id: number;
  description: string;
  createdAt: string;
  name: string;
  rethinkId: string;
}

export type RethinkList = {
  id: string;
  name: string;
}

export type RethinkCallListSync = {
  id: number;
  includeContacts: boolean;
  syncProgress: number;
  uploadError: string;
  sendFinishedEmail: boolean;
  rethinkListId: string;
}

export type CallTemplate = {
  id: number;
  title: string;
  body: string;
}

export type CallListImportStatus = {
  callListId: number;
  noPropertyAddresses: { address: string; state: string; zip: string }[];
  numFound: number;
  progress: number;
}

export type BaseConnectedRecord = {
  connectedRecordType: string;
  connectedRecordId: number;
}

export type ConnectedRecord = BaseConnectedRecord & {
  id?: number;
  label: string;
  link?: string;
}

export type Activity = {
  callListName: string;
  callResult: string;
  completedAt?: string;
  completed?: boolean;
  connectedRecordsAttributes: ConnectedRecord[];
  createdAt: string;
  description: string;
  dueDate?: string;
  email?: string;
  emailSubject: string;
  endTime?: string;
  id: number;
  isTask: boolean;
  location: string;
  name: string;
  phoneNumber?: string;
  priority: string;
  rethinkId?: string;
  script?: string;
  startTime?: string;
  syncedWithRethinkAt?: string;
  type: string;
};

export type Company = {
  id: number;
  name: string;
  type: string;
  tags: string[];
};

export type Contact = {
  activities: Activity[];
  addresses: ContactAddress[];
  birthday?: string;
  callListItems?: CallListItem[];
  company: any | null;
  contactType: string;
  emails: ContactEmail[];
  firstName: string;
  id: number;
  jobTitle?: string;
  lastName: string;
  name: string;
  phoneNumbers: ContactPhoneNumber[];
  rethinkId: string | null;
  syncedWithRethinkAt: string | null;
  tags: string[];
  type: typeof CONNECT_PERSON_TYPE | typeof CONNECT_COMPANY_TYPE;
}

export type ContactEmail = {
  connectRankScore: number;
  email: string;
  id: number;
  _destroy?: boolean;
}

export type ContactPhoneNumber = {
  connectRankScore: number;
  formatted: string;
  id: number;
  label: string;
  number: string;
  _destroy?: boolean;
}

export type ContactAddress = {
  addressOne: string;
  addressTwo: string | null;
  city: string;
  connectRankScore: number;
  id: number;
  label: string;
  primary: boolean;
  state: string;
  twoLine: string[];
  zip: string;
  _destroy?: boolean;
}

export type AiRelatedCompany = {
  id: string;
  name: string;
  website: string;
  relation: string;
  reasoning: string;
  phoneNumbers: ContactPhoneNumber[];
  feedback: Feedback;
}

export type Feedback = {
  id: string;
  rating: number;
  comment: string;
}

export type OCFiling = {
  date: string;
  id: number;
  opencorporatesUrl: string;
  title: string;
  uid: string;
}

export type OCMember = {
  name: string;
  position: string;
  address: string;
}

export type OCRegisteredAddress = {
  country: string;
  locality: string;
  postalCode: string;
  region: string;
  streetAddress: string;
}

export type OCOfficer = {
  address: string;
  name: string;
  position: string;
}

export type OCSource = {
  publisher: string;
  url: string;
}

export type OCCompany = {
  agentAddress: string;
  agentName: string;
  companyNumber: string;
  companyType: string;
  createdAt: string;
  currentStatus: string;
  dissolutionDate: string;
  filings: OCFiling[];
  inactive: boolean;
  incorporationDate: string;
  industryCodes: string[];
  jurisdictionCode: string;
  members: OCMember[];
  name: string;
  officers: OCOfficer[];
  opencorporatesUrl: string;
  registeredAddress: OCRegisteredAddress;
  registryUrl: string;
  source: OCSource;
}

export type FAOwner = {
  firstName: string;
  fullName: string;
  isCorporation: boolean;
  lastName: string;
  middleName: string;
}

export type FAAddress = {
  address: string;
  city: string;
  state: string;
  zip: string;
}

export type Radius = {
  radiusInMiles: number;
  totalHouseholds: number;
  totalPopulation: number;
  populationWhite: number;
  populationBlack: number;
  populationHispanic: number;
  populationPacificIslander: number;
  populationAsian: number;
  populationAmericanIndian: number;
  populationOther: number;
  personsPerHousehold: number;
  averageHouseholdIncome: number;
  averageHouseValue: number;
  averageAge: number;
  averageAgeMale: number;
  averageAgeFemale: number;
}

export type DemographicsReport = {
  latitude: number;
  longitude: number;
  radii: Radius[];
}

export type Mortgage = {
  adjustableRateChangeDate: string;
  adjustableRateIndex: string;
  amount: number;
  changeIndex: number;
  detailedLoanType: string;
  interestOnlyPeriod: number;
  interestRate: number;
  interestRateType: string;
  lenderName: string;
  loanType: string;
  maximumInterestRate: number;
  minimumInterestRate: number;
  recordingDate: string;
  subordinateLoan: boolean;
  term: number;
  titleCompany: string;
}

export type PropertyLoan = {
  amount: number;
  lender: string;
  term: number;
  mortgageInterestRate: number;
  startDate: string;
  endDate: string;
}

export type PropertyRecord = {
  auctionAddress: string;
  auctionCity: string;
  auctionDate: string;
  acutionMinimumBidAmount: number;
  auctionMinimumBidAmount: number;
  buyerOrBorrower: string[];
  currentLender: string;
  date: string;
  documentType: string;
  documentTypeDescription: string;
  mortgages: Mortgage[];
  recordType: string;
  sellers: string[];
  subdivisionName: string;
  titleCompany: string;
  transactionType: string;
  transactionTypeDescription: string;
  type: string;
}

export type Ownerships = {
  contact: Contact;
  assessorMatch: boolean;
  id: number;
  label: string;
  llcMatch: boolean;
  aiSources?: string[];
  aiSourced: boolean;
  aiVerified: boolean;
  verified: boolean;
  feedback: Feedback;
}

type TenantContact = {
  contactName: string;
  contactJobTitle: string;
  emails: string[];
  phoneNumbers: string[];
}

export type Tenant = {
  companyName: string;
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  state: string;
  zip: string;
  employeeSize: number;
  salesRevenue: string;
  sic1: string;
  industry1: string;
  website: string;
  contacts: TenantContact[];
}

export type Tag = {
  id: number;
  name: string;
  createdAt: string;
  tagCount: number;
  updatedAt: string;
}

export type Profile = {
  brokerSpecialties?: string[];
  city?: string;
  companyIsSelf: boolean;
  companyName?: string;
  countryId: number;
  id: number;
  investorTypes?: string[];
  products: { prospecting: boolean, exclusiveListings: boolean };
  otherIndustry: string;
  primaryRole: string;
  regions: Region[];
  state?: string;
  subscriptionRenewalDate: string;
  trialUser: boolean;
  userType: string;
  nominatimUrl: string;
}

export type Region = {
  id: number;
  state: string;
  county: string;
}

export type ProspectAccountMember = {
  name: string;
  id: number;
  currentUser: boolean;
  status: string;
}

export type StripeProduct = {
  displayName: string;
  annual?: boolean;
  credits: number;
  trialCredits: number;
  price: number;
  discountedPrice?: number;
  regions: number;
  entitlements: string[];
  stripePriceId: string;
  stripeProductId: string;
}

export type ProductPlan = {
  bestValue?: boolean;
  title: string;
  subtitle: string;
  icon: string;
  checkoutKey: string;
  price: number;
  productKey: string;
  discountedPrice?: number;
  bullets: { icon: string, title: string, text: string }[];
}

export type Product = {
  products: {
    prospecting: boolean;
    exclusiveListings: boolean;
  };
}

export type Avatar = {
  image: string;
  initials: string;
  tooltip: string;
}

export type OwnershipsResponse = {
  "connect/ownerships": Ownerships[];
  "connent/ocCompany": OCCompany;
  "connect/aiRelatedCompanies": AiRelatedCompany[];
}

export type TagsResponse = {
  "connect/tags": Tag[];
};

export type TagResponse = {
  "connect/tag": Tag;
};

type notificationPreferenceAttributes = {
  matchesDigestEmail?: boolean;
  matchesDigestSchedule?: 'daily' | 'semiweekly' | 'weekly';
  offMarketPropertiesDigestEmail?: boolean;
  offMarketPropertiesDigestSchedule?: 'weekly' | 'monthly';
}

export type UpdateProfileParams = {
  companyIsSelf?: boolean;
  companyName?: string;
  countryId?: number;
  city?: string;
  notificationPreferenceAttributes?: notificationPreferenceAttributes;
  state?: string;
  investorTypes?: string[];
  brokerSpecialties?: string[];
  otherIndustry?: string;
}

export type FetchLastFetchedAtResponse = {
  lastFetched: Date | null;
  remainingCredits: number;
}

export type SearchContactsResponse = {
  companies: {
    hits: Company[];
    total: number;
  };
  contacts: {
    hits: Contact[];
    total: number;
  };
}

export type FetchCallListItemsResponse = {
  data: CallListItem[];
}

export type GetCallConsoleItemResponse = {
  activities: Activity[];
  callList: CallList;
  index: number;
  insightsProperty: InsightsProperty;
  lastFetched: string | null;
  recordId: number;
  recordType: string;
  rethink_property_id: string;
}

export type CreateAddressImportResponse = {
  errors?: {
    invalidHeaders?: string[];
    invalidFileSize?: string;
  };
  id: number;
}

export type DataExportTemplateResponse = {
  dataExportTemplate: DataExportTemplate;
}

export type NotificationPreferences = {
  matchesDigestEmail: boolean;
  matchesDigestSchedule: "daily" | "semiweekly" | "weekly";
  offMarketPropertiesDigestEmail: boolean;
  offMarketPropertiesDigestSchedule: "weekly" | "monthly";
}

export type SavedSearch = {
  createdAt: string;
  filterSummary: { label: string, icon: string, keys: string[] }[];
  id: number;
  insightsPropertyIds: { [key: string]: { addedAt: string } } | null; // the key represents the insightsPropertyId
  lastCheckedForNewProperties: string;
  name: string;
  offMarketSearch: boolean;
  updatedAt: string;
}

export type MapBounds = {
  north: number;
  east: number;
  south: number;
  west: number;
};

type MultiPolygon = {
  type: 'MultiPolygon';
  coordinates: number[][][][];
}

type Polygon = {
  type: 'Polygon';
  coordinates: number[][];
};

export type LocationsAttributes = {
  name: string;
  polygon_search: boolean;
  polygonGeojson: (Polygon | MultiPolygon) & { label?: string };
  id?: string;
};

export type Criteria = {
  airConditioningId?: string;
  apn?: string;
  assetClassIds?: number[];
  bookmarked?: boolean;
  buildingClassA?: boolean;
  buildingClassB?: boolean;
  buildingClassC?: boolean;
  buildingCondition?: string;
  callListPropertyIds?: string[];
  corporateOwned?: string;
  filterSummary?: { label: string; icon: string; keys: string[] }[];
  includeUnderContract?: boolean; // Exclusive Listings only
  lenderName?: string;
  loanType?: string;
  locationsAttributes?: LocationsAttributes[];
  mapBounds?: MapBounds;
  maxBuildingSizeSf?: number;
  maxCapRate?: number;
  maxCommercialUnits?: number;
  maxFloorAreaRatio?: number;
  maxImprovementAssessedValue?: number;
  maxLandAssessedValue?: number;
  maxLastTransactionAmount?: number;
  maxLastTransactionAmountPerSf?: number;
  maxLoanAmount?: number;
  maxLoanBeginningDate?: string;
  maxLoanInterestRate?: number;
  maxLoanMaturityDate?: string;
  maxLoanResetDate?: string;
  maxLoanStartingDate?: string;
  maxLotDepth?: number;
  maxLotFrontage?: number;
  maxLotSizeAcres?: number;
  maxNumberOfBathrooms?: number;
  maxNumberOfBedrooms?: number;
  maxNumberOfBuildings?: number;
  maxNumberOfStories?: number;
  maxNumberOfUnits?: number;
  maxResidentialUnits?: number;
  maxSalePrice?: number;
  maxTaxAmount?: number;
  maxTaxDelinquentYear?: number;
  maxTenantsCount?: number;
  maxTotalAssessedValue?: number;
  maxTransactionDate?: string;
  maxVacancyPct?: number; // Exclusive Listings only
  minBuildingSizeSf?: number;
  minCapRate?: number;
  minCommercialUnits?: number;
  minFloorAreaRatio?: number;
  minImprovementAssessedValue?: number;
  minLandAssessedValue?: number;
  minLastTransactionAmount?: number;
  minLastTransactionAmountPerSf?: number;
  minLikelyToRefiRank?: number;
  minLikelyToSellRank?: number;
  minLoanAmount?: number;
  minLoanBeginningDate?: string;
  minLoanInterestRate?: number;
  minLoanMaturityDate?: string;
  minLoanResetDate?: string;
  minLoanStartingDate?: string;
  minLotDepth?: number;
  minLotFrontage?: number;
  minLotSizeAcres?: number;
  minNumberOfBathrooms?: number;
  minNumberOfBedrooms?: number;
  minNumberOfBuildings?: number;
  minNumberOfStories?: number;
  minNumberOfUnits?: number;
  minResidentialUnits?: number;
  minSalePrice?: number;
  minTaxAmount?: number;
  minTaxDelinquentYear?: number;
  minTenantsCount?: number;
  minTotalAssessedValue?: number;
  minTransactionDate?: string;
  minYearBuilt?: number;
  minYearRenovated?: number;
  offset?: number;
  onMarket?: boolean;
  onMarketDuration?: number;
  opportunityZone?: boolean;
  ownerAddress?: string;
  ownerCity?: string;
  ownerName?: string;
  ownerOccupied?: string;
  ownerState?: string;
  ownerZip?: string;
  propertyIds?: number[];
  propertyUse?: number; // Exclusive Listings only
  qoz?: boolean;
  recentPriceReduction?: boolean; // Exclusive Listings only
  roofCoverType?: string;
  roofType?: string;
  sortBy?: string;
  tenancyId?: number; // Exclusive Listings only
  tenantCompanyName?: string;
  tenantEmployeeSizes?: string[];
  tenantSalesRevenues?: string[];
  tenantSicCodes?: string[];
  type?: string;
  zoning?: string;
};

export type AssetClass = {
  id: number;
  label: string;
  value: number;
  subclasses: AssetClassSubClass[];
}

export type AssetClassSubClass = {
  id: number;
  label: string;
  value: number;
  parentId: number;
}

export type MailersExport = {
  createdAt: string;
  zipFileFileSize: number | null;
  exportError: string | null;
  id: number;
  name: string;
  lastDownloaded: string | null;
}

export type MailersTemplate = {
  id: number;
  title: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export type FetchMailersTemplatesResponse = {
  templates: MailersTemplate[];
  header: MailersHeader;
  total: number;
}

export type CreateMailersTemplateResponse = {
  path: string;
}

export type PreviewMailerResponse = {
  body: string;
  index: number;
  itemId: number;
  property: ConnectProperty;
}

export type MailersHeader = {
  id?: number;
  htmlLeft: string;
  htmlRight: string;
  logoUrl: string | null;
}
